// services
import * as api from 'js/common/services/api';

import * as backboneReduxSync from '../backboneReduxSync';
const {
  actionTypes: { SYNC_CHANGES_FROM_BACKBONE, SYNC_FROM_BACKBONE },
} = backboneReduxSync;

const RESET = 'viewer/RESET';
const SET_EMAIL = 'viewer/SET_EMAIL';
const SET_PASSWORD = 'viewer/SET_PASSWORD';
const SET_PHONE_NUMBER = 'viewer/SET_PHONE_NUMBER';
const SET_DATE_FORMAT = 'viewer/SET_DATE_FORMAT';
const SET_TIME_ZONE = 'viewer/SET_TIME_ZONE';
const SET_ATLAS_GO_TO_DISABLED = 'viewer/SET_ATLAS_GO_TO_DISABLED';

interface ViewerState {
  primaryEmail?: string;
  phoneNumber?: string;
  dateFormatCode?: string;
  timeZoneDisplay?: string;
  timeZoneId?: string;
  currentUserCountryCode?: string;
  goToDisabled?: boolean;
}
const initialState: ViewerState = {};

const backboneToReduxKeyMapping = {
  DATE_FORMAT_CODE: 'dateFormatCode',
  FIRST_NAME: 'firstName',
  HAS_MULTI_FACTOR_AUTH: 'hasMultiFactorAuth',
  HAS_ACCOUNT_MULTI_FACTOR_AUTH: 'hasAccountMultiFactorAuth',
  HAS_ORG_GROUP_CREATOR: 'hasOrgGroupCreator',
  HAS_ORG_USER_ADMIN: 'hasOrgUserAdmin',
  IS_USER_ADMIN: 'isUserAdmin',
  LAST_NAME: 'lastName',
  MOBILE_PHONE_NUMBER: 'phoneNumber',
  PRIMARY_EMAIL: 'primaryEmail',
  TIME_FORMAT_CODE: 'timeFormatCode',
  TIME_ZONE_DISPLAY: 'timeZoneDisplay',
  TIME_ZONE_ID: 'timeZoneId',
  USERNAME: 'username',
  USER_ID: 'id',
  USER_ORG_IDS: 'orgIds',
  IS_AUTOMATION_ADMIN: 'isAutomationAdmin',
  IS_MONITORING_ADMIN: 'isMonitoringAdmin',
  HAS_GLOBAL_READ_ONLY: 'hasGlobalReadOnly',
  HAS_GLOBAL_OWNER: 'hasGlobalOwner',
  HAS_GLOBAL_ATLAS_ADMIN: 'hasGlobalAtlasAdmin',
  HAS_GLOBAL_MONITORING_ADMIN: 'hasGlobalMonitoringAdmin',
  HAS_GLOBAL_AUTOMATION_ADMIN: 'hasGlobalAutomationAdmin',
  HAS_GLOBAL_FEATURE_FLAG_ADMIN: 'hasGlobalFeatureFlagAdmin',
  IS_GROUP_OWNER: 'isGroupOwner',
  CAN_USE_AUTOMATION_LOG_COLLECTION: 'canUseAutomationLogCollection',
  CURRENT_GROUP_ID: 'ccid',
  CURRENT_ORG_ID: 'currentOrgId',
  MARKETPLACE_REGISTRATION_DATA: 'marketplaceRegistrationData',
  USER_COUNTRY_CODE: 'currentUserCountryCode',
  GO_TO_DISABLED: 'goToDisabled',

  // For admin
  'userRoles.hasGlobalOwner': 'hasGlobalOwner',
  'userRoles.hasGlobalMonitoringAdmin': 'hasGlobalMonitoringAdmin',
  'userRoles.hasGlobalProactiveSupportAdmin': 'hasGlobalProactiveSupportAdmin',
  'userRoles.hasGlobalUserAdmin': 'hasGlobalUserAdmin',
  'userRoles.hasGlobalReadOnly': 'hasGlobalReadOnly',
  'userRoles.hasGlobalAtlasAdmin': 'hasGlobalAtlasAdmin',
  'userRoles.hasGlobalFeatureFlagAdmin': 'hasGlobalFeatureFlagAdmin',
};

// Reducer
// eslint-disable-next-line no-multi-assign
export default function viewerReducer(state = initialState, action) {
  switch (action.type) {
    case RESET: {
      // Sanitize property so it's always an array.
      action.payload.orgIds = action.payload.orgIds || [];

      return action.payload;
    }
    case SET_EMAIL: {
      const nextState = { ...state };
      nextState.primaryEmail = action.payload;
      return nextState;
    }
    case SET_PHONE_NUMBER: {
      const nextState = { ...state };
      nextState.phoneNumber = action.payload;
      return nextState;
    }
    case SET_DATE_FORMAT: {
      const nextState = { ...state };
      nextState.dateFormatCode = action.payload;
      return nextState;
    }
    case SET_TIME_ZONE: {
      const nextState = { ...state };
      const { timeZoneDisplay, timeZoneId } = action.payload;
      nextState.timeZoneDisplay = timeZoneDisplay;
      nextState.timeZoneId = timeZoneId;
      return nextState;
    }
    case SET_ATLAS_GO_TO_DISABLED: {
      const { goToDisabled } = action.payload;
      return {
        ...state,
        goToDisabled,
      };
    }
    case SYNC_FROM_BACKBONE: {
      return backboneReduxSync.handleSyncFromBackbone(state, action, backboneToReduxKeyMapping);
    }
    case SYNC_CHANGES_FROM_BACKBONE: {
      return backboneReduxSync.handleSyncChangesFromBackbone(state, action, backboneToReduxKeyMapping);
    }
    default:
      return state;
  }
}

// Selectors

export const getFirstName = (state) => state.viewer.firstName;
export const getLastName = (state) => state.viewer.lastName;
export const getUsername = (state) => state.viewer.username;
export const getPrimaryEmail = (state) => state.viewer.primaryEmail;
export const getOrgIds = (state) => state.viewer.orgIds;
export const getLastAuthAddr = (state) => state.viewer.lastAuthAddr;
export const getTimeZoneDisplay = (state) => state.viewer.timeZoneDisplay;
export const getDateFormatCode = (state) => state.viewer.dateFormatCode || '';
export const getTimeFormatCode = (state) => state.viewer.timeFormatCode;
export const getTimeZoneId = (state) => state.viewer.timeZoneId || '';
export const getEmployerName = (state) => state.viewer.employer;
export const getPhoneNumber = (state) => state.viewer.phoneNumber || '';
export const hasMultiFactorAuth = (state) => state.viewer.hasMultiFactorAuth;
export const hasAccountMultiFactorAuth = (state) => state.viewer.hasAccountMultiFactorAuth;
export const canUseAutomationLogCollection = (state) => state.viewer.canUseAutomationLogCollection;
export const getId = (state) => state.viewer.id;
export const getCreated = (state) => state.viewer.created;
export const getLastAuthMethod = (state) => state.viewer.lastAuthMethod;
export const getCurrentOrgId = (state) => state.viewer.currentOrgId;
export const getCurrentProjectId = (state) => state.viewer.ccid;
export const getMarketplaceRegistrationData = (state) => state.viewer.marketplaceRegistrationData;
export const getCurrentUserCountryCode = (state) => state.viewer.currentUserCountryCode;
export const isGoToDisabled = (state) => state.viewer.goToDisabled;

// Role related selectors
export const isGlobalOwner = (state) => state.viewer.hasGlobalOwner;
export const isGlobalProactiveSupportAdmin = (state) => state.viewer.hasGlobalProactiveSupportAdmin;
export const isGlobalAtlasAdmin = (state) => state.viewer.hasGlobalAtlasAdmin;
export const isGlobalAutomationAdmin = (state) => state.viewer.hasGlobalAutomationAdmin;
export const isGlobalReadOnly = (state) => state.viewer.hasGlobalReadOnly;
export const isGlobalFeatureFlagAdmin = (state) => state.viewer.hasGlobalFeatureFlagAdmin;
export const isOrgReadOnly = (state) => state.viewer.hasOrgReadOnly;
export const isGlobalAccountSuspensionAdmin = (state) => state.viewer.hasGlobalAccountSuspensionAdmin;
export const isGlobalBillingAdmin = (state) => state.viewer.hasGlobalBillingAdmin;
export const isGlobalUserAdmin = (state) => state.viewer.hasGlobalUserAdmin;
export const isGlobalMonitoringAdmin = (state) => state.viewer.hasGlobalMonitoringAdmin;
export const isOrgOwner = (state) => state.viewer.hasOrgOwner;
export const isOrgBillingAdmin = (state) => state.viewer.hasOrgBillingAdmin;
export const isOrgGroupCreator = (state) => state.viewer.hasOrgGroupCreator;
export const isOrgUserAdmin = (state) => state.viewer.hasOrgUserAdmin;
export const isGroupOwner = (state) => state.viewer.isGroupOwner;
export const isAnyGroupRole = (state) => state.viewer.isAnyGroupRole;
export const isUserAdmin = (state) => state.viewer.isUserAdmin;
export const isAutomationAdmin = (state) => state.viewer.isAutomationAdmin;
export const isMonitoringAdmin = (state) => state.viewer.isMonitoringAdmin;

// Action Creators

export const resetState = (payload) => ({
  type: RESET,
  payload,
});

const setPassword = (payload) => ({
  type: SET_PASSWORD,
  payload,
});

const setEmail = (payload) => ({
  type: SET_EMAIL,
  payload,
});

const setPhoneNumber = (payload) => ({
  type: SET_PHONE_NUMBER,
  payload,
});

const setDateFormat = (payload) => ({
  type: SET_DATE_FORMAT,
  payload,
});

const setTimezone = ({ timeZoneDisplay, timeZoneId }) => ({
  type: SET_TIME_ZONE,
  payload: {
    timeZoneDisplay,
    timeZoneId,
  },
});

const setIsAtlasGoToDisabled = ({ isDisabled }) => ({
  type: SET_ATLAS_GO_TO_DISABLED,
  payload: {
    goToDisabled: isDisabled,
  },
});

export const updateEmail = (email) => (dispatch) => {
  return api.user.updateEmail(email).then(({ emailAddress }) => dispatch(setEmail(emailAddress)));
};

export const updatePhoneNumber = (phone) => (dispatch) => {
  return api.user
    .updateMobilePhoneNumber(phone)
    .then(({ mobilePhoneNumber }) => dispatch(setPhoneNumber(mobilePhoneNumber)));
};

export const updatePassword =
  ({ currentPassword, newPassword, confirmPassword }) =>
  (dispatch) => {
    return api.user
      .updatePassword({ currentPassword, newPassword, confirmPassword })
      .then((passwordResponse) => dispatch(setPassword(passwordResponse)));
  };

export const updateDateFormat = (dateFormat) => (dispatch) => {
  return api.settings.updateDateFormat(dateFormat).then(({ value }) => dispatch(setDateFormat(value)));
};

export const updateTimeZone =
  ({ canonicalName, displayName }) =>
  (dispatch) => {
    return api.settings
      .updateTimeZone({
        timeZoneId: canonicalName,
      })
      .then(() =>
        dispatch(
          setTimezone({
            timeZoneId: canonicalName,
            timeZoneDisplay: displayName,
          })
        )
      );
  };

export const updateIsGoToDisabled =
  ({ isDisabled }) =>
  (dispatch) => {
    return api.settings
      .updateAtlasGoToSettings({
        isDisabled,
      })
      .then(() =>
        dispatch(
          setIsAtlasGoToDisabled({
            isDisabled,
          })
        )
      );
  };
